import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Premium count-up number component
const AnimatedNumber = ({ value = 0, duration = 1200, trigger = true, suffix = '', decimals = 0 }) => {
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!trigger) { setDisplay(0); return; }
        let startTime = null;
        let raf;
        const animate = (ts) => {
            if (!startTime) startTime = ts;
            const progress = Math.min((ts - startTime) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = eased * value;
            setDisplay(decimals > 0 ? parseFloat(current.toFixed(decimals)) : Math.round(current));
            if (progress < 1) raf = requestAnimationFrame(animate);
            else setDisplay(value);
        };
        raf = requestAnimationFrame(animate);
        return () => raf && cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, duration, trigger]);

    return (
        <>
            {decimals > 0
                ? display.toFixed(decimals).toLocaleString('en-IN')
                : display.toLocaleString('en-IN')}
            {suffix}
        </>
    );
};

const LiveVotingForCader = () => {
    const navigate = useNavigate();
    const { appId, userId, total_voters, language, userType, boothNo } = useAppId();
    const lang = language || 'en';
    const roleCode = (userType || '').toUpperCase();
    const isShaktiPramukh = roleCode === 'SP' || roleCode === 'SHAKTI_KENDRA';
    const isWarroomPramukh = roleCode === 'WP' || roleCode === 'WARROOM_PRAMUKH';
    const isBoothPramukh = roleCode === 'BP' || roleCode === 'BOOTH_PRAMUKH';
    const isSahBoothPramukh = roleCode === 'BS' || roleCode === 'SAH_BOOTH_PRAMUKH';
    const isBoothCaptain = roleCode === 'BC' || roleCode === 'BOOTH_CAPTAIN';
    const isVoterCaptain = roleCode === 'VC' || roleCode === 'VOTER_CAPTAIN';
    const goToMyBoothList = isShaktiPramukh || isWarroomPramukh;
    const goToBoothVoterDirect = isBoothPramukh || isSahBoothPramukh || isBoothCaptain;
    const showMyBoothMenu = goToMyBoothList || goToBoothVoterDirect;
    const showMyVoterMenu = isVoterCaptain;

    const handleMyBoothClick = () => {
        if (goToBoothVoterDirect) {
            const source = boothNo || localStorage.getItem('boothNo') || '';
            const userBooth = String(source).split(',').map((b) => b.trim()).filter(Boolean)[0];
            if (userBooth) {
                navigate('/dis_booth_wise_live_voting_voter', { state: { boothNo: userBooth } });
            }
            return;
        }
        navigate('/dis_my_booth_live_voting');
    };

    const t = {
        en: {
            pageTitle: "Live Voting",
            overview: "OVERVIEW",
            liveZone: "Live Voting Zone",
            live: "LIVE",
            voting: "Voting",
            total: "Total",
            done: "Done",
            remain: "Remain",
            phonebookTitle: "My Phonebook Live Voting",
            reportingTitle: "Reporting Menu",
            myBooth: "My Booth",
            myVoter: "My Voter"
        },
        hi: {
            pageTitle: "लाइव वोटिंग",
            overview: "अवलोकन",
            liveZone: "लाइव वोटिंग ज़ोन",
            live: "लाइव",
            voting: "वोटिंग",
            total: "कुल",
            done: "हो गया",
            remain: "बाकी",
            phonebookTitle: "मेरी फोनबुक लाइव वोटिंग",
            reportingTitle: "रिपोर्टिंग मेनू",
            myBooth: "मेरा बूथ",
            myVoter: "मेरे मतदाता"
        },
        gu: {
            pageTitle: "લાઈવ વોટિંગ",
            overview: "OVERVIEW",
            liveZone: "લાઈવ મતદાન ઝોન",
            live: "LIVE",
            voting: "વોટિંગ",
            total: "ટોટલ",
            done: "થયેલ",
            remain: "બાકી",
            phonebookTitle: "મારી ફોનબુક નું લાઈવ મતદાન",
            reportingTitle: "રિપોર્ટિંગ મેનુ",
            myBooth: "મારા બૂથ",
            myVoter: "મારા મતદાર"
        }
    };

    const txt = t[lang] || t.gu;

    const [currentTime, setCurrentTime] = useState("");
    const [currentDate, setCurrentDate] = useState("");
    const [votingData, setVotingData] = useState({ voted: 0 });
    const [phonebookData, setPhonebookData] = useState({ total: 0, voting: 0, remain: 0 });
    const [loading, setLoading] = useState(true);
    const [chartsReady, setChartsReady] = useState(false);

    const totalVotersVal = total_voters ? parseInt(total_voters) : 67185;

    // Real-time Clock
    useEffect(() => {
        const timer = setInterval(() => {
            const date = new Date();
            setCurrentTime(date.toLocaleTimeString('en-US', { hour12: true }));
            setCurrentDate(date.toLocaleDateString('en-US', {
                weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
            }));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    // Trigger chart animations after data loads
    useEffect(() => {
        if (!loading) {
            const timer = setTimeout(() => setChartsReady(true), 200);
            return () => clearTimeout(timer);
        }
        setChartsReady(false);
    }, [loading]);

    useEffect(() => {
        if (!appId || !userId) { navigate('/login'); return; }

        const fetchLiveVoting = async () => {
            try {
                const response = await axios.post(`${BASE_URL}/api/get_LiveVoting_Dashboard_for_admin`, {
                    app_id: appId,
                    user_id: userId
                });
                if (response.data.success && response.data.data) {
                    if (response.data.data.result && response.data.data.result.length > 0) {
                        setVotingData({ voted: response.data.data.result[0].total_voting_count });
                    }
                    if (response.data.data.result2 && response.data.data.result2.length > 0) {
                        setPhonebookData(response.data.data.result2[0]);
                    }
                }
            } catch (error) {
                console.error("Error fetching live voting data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchLiveVoting();
        const dataInterval = setInterval(fetchLiveVoting, 30000);
        return () => clearInterval(dataInterval);
    }, [appId, userId, navigate]);

    // Calculations
    const remainingVotes = totalVotersVal - votingData.voted;
    const votingPercentage = totalVotersVal > 0
        ? parseFloat(((votingData.voted / totalVotersVal) * 100).toFixed(1))
        : 0;

    // Circular Progress Math
    const circleRadius = 80;
    const circleCircumference = 2 * Math.PI * circleRadius;
    const strokeDashoffset = circleCircumference - (votingPercentage / 100) * circleCircumference;

    return (
        <div className="live-voting-page">
            <div className="header-wrapper">
                <Header title={txt.pageTitle} />
            </div>

            <div className="content-container container-fluid p-2 p-md-4 mx-auto">
                <div className="two-column-layout">

                    {/* ======= LEFT COLUMN ======= */}
                    <div className="left-column">

                        {/* 1. Clock Card */}
                        <div className="clock-card fade-in">
                            <div className="clock-glow"></div>
                            <div className="date-text">{currentDate || "Loading..."}</div>
                            <div className="time-text">{currentTime || "00:00:00"}</div>
                            <div className="clock-divider"></div>
                            <div className="clock-ticker">
                                <span className="ticker-dot"></span>
                                <span className="ticker-label">Live Data · Auto-refreshes every 30s</span>
                            </div>
                        </div>

                        {/* Overview Header */}
                        <div className="overview-header-row fade-in" style={{ animationDelay: '0.1s' }}>
                            <div>
                                <div className="overview-label">{txt.overview}</div>
                                <h4 className="zone-title">{txt.liveZone}</h4>
                            </div>
                            <div className="live-badge">
                                <span className="live-dot"></span>
                                {txt.live}
                            </div>
                        </div>

                        {/* 2. Main Stats Card */}
                        <div className="main-stats-card fade-in" style={{ animationDelay: '0.15s' }}>
                            <div className="circle-container">
                                <div className="circle-glow-ring"></div>
                                <svg className="progress-svg" width="200" height="200" viewBox="0 0 200 200">
                                    <defs>
                                        <linearGradient id="voteGradientCader" x1="0%" y1="0%" x2="100%" y2="100%">
                                            <stop offset="0%" stopColor="#4ade80" />
                                            <stop offset="100%" stopColor="#16a34a" />
                                        </linearGradient>
                                        <filter id="glowCader">
                                            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                            <feMerge>
                                                <feMergeNode in="coloredBlur" />
                                                <feMergeNode in="SourceGraphic" />
                                            </feMerge>
                                        </filter>
                                    </defs>
                                    <circle cx="100" cy="100" r={circleRadius} stroke="#f1f5f9" strokeWidth="12" fill="none" />
                                    <circle
                                        cx="100" cy="100"
                                        r={circleRadius}
                                        stroke="url(#voteGradientCader)"
                                        strokeWidth="12"
                                        fill="none"
                                        strokeLinecap="round"
                                        strokeDasharray={circleCircumference}
                                        strokeDashoffset={chartsReady ? strokeDashoffset : circleCircumference}
                                        filter="url(#glowCader)"
                                        style={{
                                            transition: 'stroke-dashoffset 1.4s cubic-bezier(0.16, 1, 0.3, 1)',
                                            transform: 'rotate(-90deg)',
                                            transformOrigin: '50% 50%'
                                        }}
                                    />
                                </svg>
                                <div className="circle-content">
                                    <span className="percent-text">
                                        <AnimatedNumber
                                            value={loading ? 0 : votingPercentage}
                                            trigger={chartsReady}
                                            duration={1400}
                                            decimals={1}
                                        />
                                        <span className="percent-sign">%</span>
                                    </span>
                                    <span className="voting-lbl">{txt.voting}</span>
                                </div>
                            </div>

                            <div className="stats-boxes">
                                <div className="stat-box border-blue">
                                    <div className="stat-box-inner">
                                        <span className="s-num text-blue">
                                            <AnimatedNumber value={totalVotersVal} trigger={chartsReady} />
                                        </span>
                                        <span className="s-lbl">{txt.total}</span>
                                    </div>
                                    <div className="stat-shimmer-bar fill-blue" style={{ width: chartsReady ? '100%' : '0%' }}></div>
                                </div>
                                <div className="stat-box border-green">
                                    <div className="stat-box-inner">
                                        <span className="s-num text-green">
                                            <AnimatedNumber value={loading ? 0 : votingData.voted} trigger={chartsReady} />
                                        </span>
                                        <span className="s-lbl">{txt.done}</span>
                                    </div>
                                    <div className="stat-shimmer-bar fill-green" style={{ width: chartsReady ? `${votingPercentage}%` : '0%' }}></div>
                                </div>
                                <div className="stat-box border-orange">
                                    <div className="stat-box-inner">
                                        <span className="s-num text-orange">
                                            <AnimatedNumber value={loading ? 0 : remainingVotes} trigger={chartsReady} />
                                        </span>
                                        <span className="s-lbl">{txt.remain}</span>
                                    </div>
                                    <div className="stat-shimmer-bar fill-orange" style={{ width: chartsReady ? `${100 - votingPercentage}%` : '0%' }}></div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ======= RIGHT COLUMN ======= */}
                    <div className="right-column">

                        {/* 3. Phonebook Card */}
                        <div
                            className="custom-panel fade-in"
                            style={{ animationDelay: '0.2s' }}
                            onClick={() => navigate("/dis_phonebook_wise_live_voting_voter", { state: { userId } })}
                        >
                            <div className="panel-header">
                                <span>{txt.phonebookTitle}</span>
                                <div className="panel-header-arrow">
                                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                    </svg>
                                </div>
                            </div>
                            <div className="panel-body-horizontal">
                                <div className="color-stat-box">
                                    <div className="c-head bg-blue">{txt.total}</div>
                                    <div className="c-body text-blue">
                                        <AnimatedNumber value={loading ? 0 : phonebookData.total} trigger={chartsReady} />
                                    </div>
                                </div>
                                <div className="color-stat-box">
                                    <div className="c-head bg-green">{txt.done}</div>
                                    <div className="c-body text-green">
                                        <AnimatedNumber value={loading ? 0 : phonebookData.voting} trigger={chartsReady} />
                                    </div>
                                </div>
                                <div className="color-stat-box">
                                    <div className="c-head bg-red">{txt.remain}</div>
                                    <div className="c-body text-red">
                                        <AnimatedNumber value={loading ? 0 : phonebookData.remain} trigger={chartsReady} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Reporting Menu - My Booth for SP/WP (list) and BP/BS/BC (direct voter) */}
                        {showMyBoothMenu && (
                            <div className="custom-panel fade-in mt-0" style={{ animationDelay: '0.3s', cursor: 'default' }}>
                                <div className="panel-header">
                                    <span>{txt.reportingTitle}</span>
                                </div>
                                <div className="panel-body-grid">
                                    <div
                                        className="menu-btn-square"
                                        onClick={handleMyBoothClick}
                                    >
                                        <div className="m-icon-wrap icon-wrap-blue">
                                            <svg width="26" height="26" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <span className="m-lbl">{txt.myBooth}</span>
                                        <div className="m-arrow">›</div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Reporting Menu - My Voter for Voter Captain */}
                        {showMyVoterMenu && (
                            <div className="custom-panel fade-in mt-0" style={{ animationDelay: '0.3s', cursor: 'default' }}>
                                <div className="panel-header">
                                    <span>{txt.reportingTitle}</span>
                                </div>
                                <div className="panel-body-grid">
                                    <div
                                        className="menu-btn-square"
                                        onClick={() => navigate('/dis_voter_captain_live_voting_voter')}
                                    >
                                        <div className="m-icon-wrap icon-wrap-green">
                                            <svg width="26" height="26" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <span className="m-lbl">{txt.myVoter}</span>
                                        <div className="m-arrow">›</div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            <style jsx>{`
                * { box-sizing: border-box; }

                .live-voting-page {
                    width: 100%;
                    min-height: 100vh;
                    background: linear-gradient(180deg, #f8fafc 0%, #eef1f7 100%);
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
                    padding-bottom: 40px;
                }

                .header-wrapper {
                    background: white;
                    padding: 0 24px;
                    position: sticky;
                    top: 0;
                    z-index: 50;
                    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
                }

                /* Layout */
                .two-column-layout {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 24px;
                    margin-top: 12px;
                }
                .left-column, .right-column {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }

                /* ======= CLOCK CARD ======= */
                .clock-card {
                    background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
                    border-radius: 20px;
                    text-align: center;
                    padding: 28px 24px 20px;
                    box-shadow: 0 4px 6px rgba(15,23,42,0.1), 0 20px 40px rgba(15,23,42,0.2);
                    border: 1px solid #334155;
                    position: relative;
                    overflow: hidden;
                }
                .clock-glow {
                    position: absolute;
                    top: -60px; left: 50%;
                    transform: translateX(-50%);
                    width: 200px; height: 200px;
                    background: radial-gradient(circle, rgba(99,102,241,0.25), transparent 70%);
                    animation: glowPulse 4s ease-in-out infinite;
                    pointer-events: none;
                }
                @keyframes glowPulse {
                    0%, 100% { opacity: 0.5; transform: translateX(-50%) scale(0.9); }
                    50% { opacity: 1; transform: translateX(-50%) scale(1.1); }
                }
                .date-text {
                    color: #94a3b8;
                    font-size: 13px;
                    font-weight: 600;
                    letter-spacing: 0.8px;
                    margin-bottom: 10px;
                    position: relative; z-index: 1;
                }
                .time-text {
                    color: #ffffff;
                    font-size: 2.6rem;
                    font-weight: 800;
                    font-family: 'SF Mono', 'Fira Code', monospace;
                    letter-spacing: 4px;
                    text-shadow: 0 0 30px rgba(99,102,241,0.5);
                    position: relative; z-index: 1;
                    animation: textShimmer 3s ease-in-out infinite;
                }
                @keyframes textShimmer {
                    0%, 100% { text-shadow: 0 0 20px rgba(99,102,241,0.4); }
                    50% { text-shadow: 0 0 40px rgba(99,102,241,0.8), 0 0 60px rgba(99,102,241,0.3); }
                }
                .clock-divider {
                    height: 1px;
                    background: linear-gradient(90deg, transparent, #334155, transparent);
                    margin: 16px 0 12px;
                }
                .clock-ticker {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 7px;
                }
                .ticker-dot {
                    width: 7px; height: 7px;
                    background: #22c55e;
                    border-radius: 50%;
                    animation: tickerBlink 1.5s ease-in-out infinite;
                    box-shadow: 0 0 8px rgba(34,197,94,0.8);
                }
                @keyframes tickerBlink {
                    0%, 100% { opacity: 1; box-shadow: 0 0 8px rgba(34,197,94,0.8); }
                    50% { opacity: 0.3; box-shadow: 0 0 2px rgba(34,197,94,0.3); }
                }
                .ticker-label {
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 600;
                    letter-spacing: 0.3px;
                }

                /* Overview Header */
                .overview-header-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    padding: 0 2px;
                }
                .overview-label {
                    color: #f59e0b;
                    font-size: 11px;
                    font-weight: 800;
                    letter-spacing: 1.5px;
                    margin-bottom: 3px;
                    text-transform: uppercase;
                }
                .zone-title {
                    margin: 0;
                    font-weight: 800;
                    color: #0f172a;
                    font-size: 1.15rem;
                    letter-spacing: -0.3px;
                }
                .live-badge {
                    background: linear-gradient(135deg, #dcfce7, #bbf7d0);
                    color: #15803d;
                    font-weight: 700;
                    font-size: 11px;
                    padding: 6px 14px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    border: 1px solid #86efac;
                    box-shadow: 0 2px 8px rgba(22,163,74,0.15);
                    animation: badgeBreath 2.5s ease-in-out infinite;
                }
                @keyframes badgeBreath {
                    0%, 100% { box-shadow: 0 2px 8px rgba(22,163,74,0.15); }
                    50% { box-shadow: 0 4px 16px rgba(22,163,74,0.3); }
                }
                .live-dot {
                    width: 8px; height: 8px;
                    background: #22c55e;
                    border-radius: 50%;
                    animation: livePulse 1.5s ease-in-out infinite;
                    box-shadow: 0 0 0 0 rgba(34,197,94,0.7);
                }
                @keyframes livePulse {
                    0% { box-shadow: 0 0 0 0 rgba(34,197,94,0.7); }
                    70% { box-shadow: 0 0 0 6px rgba(34,197,94,0); }
                    100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
                }

                /* Main Stats Card */
                .main-stats-card {
                    background: white;
                    border-radius: 20px;
                    padding: 28px 22px 22px;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(15,23,42,0.05);
                    border: 1px solid #eef1f6;
                    transition: box-shadow 0.4s cubic-bezier(0.16,1,0.3,1), transform 0.4s cubic-bezier(0.16,1,0.3,1);
                }
                .main-stats-card:hover {
                    box-shadow: 0 4px 6px rgba(15,23,42,0.04), 0 20px 40px rgba(15,23,42,0.08);
                    transform: translateY(-2px);
                }

                .circle-container {
                    display: flex;
                    justify-content: center;
                    position: relative;
                    margin-bottom: 26px;
                }
                .circle-glow-ring {
                    position: absolute;
                    top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    width: 140px; height: 140px;
                    border-radius: 50%;
                    background: radial-gradient(circle, rgba(34,197,94,0.12), transparent 70%);
                    animation: glowPulse 3s ease-in-out infinite;
                    pointer-events: none;
                }
                .circle-content {
                    position: absolute;
                    top: 50%; left: 50%;
                    transform: translate(-50%, -50%);
                    display: flex; flex-direction: column;
                    align-items: center; justify-content: center;
                }
                .percent-text {
                    font-size: 2rem;
                    font-weight: 800;
                    color: #0f172a;
                    line-height: 1;
                    letter-spacing: -1px;
                    font-variant-numeric: tabular-nums;
                }
                .percent-sign { font-size: 1rem; color: #16a34a; font-weight: 700; }
                .voting-lbl {
                    font-size: 12px;
                    font-weight: 700;
                    color: #94a3b8;
                    margin-top: 5px;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                }

                /* Stat Boxes */
                .stats-boxes {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px;
                }
                .stat-box {
                    background: #f8fafc;
                    border-radius: 14px;
                    border: 1px solid #eef1f6;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    transition: transform 0.35s cubic-bezier(0.16,1,0.3,1), box-shadow 0.35s cubic-bezier(0.16,1,0.3,1);
                    position: relative;
                }
                .stat-box:hover {
                    transform: translateY(-4px) scale(1.02);
                    box-shadow: 0 8px 20px rgba(15,23,42,0.09);
                }
                .stat-box-inner {
                    padding: 14px 10px 10px;
                    text-align: center;
                    display: flex; flex-direction: column;
                    align-items: center; gap: 4px;
                    flex: 1;
                }
                .border-blue { border-top: 3px solid #3b82f6; }
                .border-green { border-top: 3px solid #22c55e; background: #f0fdf4; }
                .border-orange { border-top: 3px solid #f59e0b; background: #fffbeb; }
                .s-num {
                    font-size: 1.2rem;
                    font-weight: 800;
                    letter-spacing: -0.5px;
                    font-variant-numeric: tabular-nums;
                }
                .s-lbl { font-size: 11px; font-weight: 700; color: #475569; }
                .text-blue { color: #1e3a8a; }
                .text-green { color: #16a34a; }
                .text-orange { color: #d97706; }
                .text-red { color: #dc2626; }

                .stat-shimmer-bar {
                    height: 4px;
                    border-radius: 0 0 2px 2px;
                    transition: width 1.3s cubic-bezier(0.16,1,0.3,1);
                    position: relative;
                    overflow: hidden;
                }
                .stat-shimmer-bar::after {
                    content: '';
                    position: absolute;
                    top: 0; left: 0;
                    width: 45%; height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.7), transparent);
                    animation: shineSweep 2.2s ease-in-out infinite;
                }
                @keyframes shineSweep {
                    0% { transform: translateX(-150%); }
                    100% { transform: translateX(350%); }
                }
                .fill-blue { background: linear-gradient(90deg, #6366f1, #3b82f6); }
                .fill-green { background: linear-gradient(90deg, #4ade80, #16a34a); }
                .fill-orange { background: linear-gradient(90deg, #fbbf24, #d97706); }

                /* Custom Panels */
                .custom-panel {
                    background: white;
                    border-radius: 20px;
                    overflow: hidden;
                    box-shadow: 0 1px 2px rgba(15,23,42,0.03), 0 8px 24px rgba(15,23,42,0.05);
                    border: 1px solid #eef1f6;
                    transition: box-shadow 0.4s cubic-bezier(0.16,1,0.3,1), transform 0.4s cubic-bezier(0.16,1,0.3,1);
                    cursor: pointer;
                    position: relative;
                }
                .custom-panel:hover {
                    box-shadow: 0 4px 6px rgba(15,23,42,0.04), 0 20px 40px rgba(15,23,42,0.08);
                    transform: translateY(-3px);
                }
                .custom-panel:active { transform: translateY(-1px) scale(0.995); }

                .panel-header {
                    background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
                    color: white;
                    padding: 16px 20px;
                    font-size: 14.5px;
                    font-weight: 700;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    letter-spacing: 0.1px;
                }
                .panel-header-arrow {
                    width: 28px; height: 28px;
                    background: rgba(255,255,255,0.1);
                    border-radius: 8px;
                    display: flex; align-items: center; justify-content: center;
                    transition: all 0.35s cubic-bezier(0.34,1.56,0.64,1);
                    flex-shrink: 0;
                }
                .custom-panel:hover .panel-header-arrow {
                    background: rgba(255,255,255,0.2);
                    transform: translateX(3px);
                }

                .panel-body-horizontal {
                    padding: 20px;
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 14px;
                }

                /* Color Stat Boxes */
                .color-stat-box {
                    border: 1px solid #eef1f6;
                    border-radius: 12px;
                    overflow: hidden;
                    text-align: center;
                    transition: transform 0.35s cubic-bezier(0.16,1,0.3,1), box-shadow 0.35s cubic-bezier(0.16,1,0.3,1);
                }
                .color-stat-box:hover {
                    transform: translateY(-3px) scale(1.02);
                    box-shadow: 0 6px 16px rgba(15,23,42,0.09);
                }
                .c-head {
                    padding: 10px 0;
                    font-size: 12px;
                    font-weight: 700;
                    color: white;
                    letter-spacing: 0.3px;
                    text-transform: uppercase;
                }
                .c-body {
                    padding: 14px 0;
                    font-size: 1.5rem;
                    font-weight: 800;
                    background: white;
                    font-variant-numeric: tabular-nums;
                }
                .bg-blue { background: linear-gradient(135deg, #3b82f6, #1d4ed8); }
                .bg-green { background: linear-gradient(135deg, #22c55e, #15803d); }
                .bg-red { background: linear-gradient(135deg, #ef4444, #dc2626); }

                /* Reporting Menu */
                .panel-body-grid {
                    padding: 20px;
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 14px;
                }
                .menu-btn-square {
                    border: 1px solid #eef1f6;
                    border-radius: 16px;
                    padding: 20px 10px 16px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.35s cubic-bezier(0.16,1,0.3,1);
                    background: #f8fafc;
                    position: relative;
                    overflow: hidden;
                    gap: 10px;
                }
                .menu-btn-square:hover {
                    background: white;
                    transform: translateY(-5px);
                    box-shadow: 0 10px 26px rgba(15,23,42,0.1);
                    border-color: #e0e7ff;
                }
                .m-icon-wrap {
                    width: 52px; height: 52px;
                    border-radius: 14px;
                    display: flex; align-items: center; justify-content: center;
                }
                .icon-wrap-blue { background: linear-gradient(135deg, #dbeafe, #bfdbfe); color: #1d4ed8; }
                .icon-wrap-green { background: linear-gradient(135deg, #d1fae5, #a7f3d0); color: #047857; }
                .m-lbl {
                    font-size: 12px;
                    font-weight: 700;
                    color: #0f172a;
                    text-align: center;
                    line-height: 1.3;
                }
                .m-arrow {
                    font-size: 16px;
                    color: #cbd5e1;
                    font-weight: 700;
                    line-height: 1;
                }
                .menu-btn-square:hover .m-arrow { color: #4338ca; transform: translateX(3px); }

                /* Animations */
                .fade-in {
                    animation: fadeInUp 0.85s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                    opacity: 0;
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(24px) scale(0.98); filter: blur(4px); }
                    to { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
                }

                /* Responsive */
                @media (max-width: 992px) {
                    .two-column-layout { grid-template-columns: 1fr; gap: 20px; }
                }
                @media (max-width: 768px) {
                    .clock-card { padding: 20px 16px 16px; }
                    .time-text { font-size: 2rem; letter-spacing: 2px; }
                    .panel-body-horizontal, .panel-body-grid { padding: 14px; gap: 10px; }
                }
                @media (max-width: 480px) {
                    .time-text { font-size: 1.7rem; }
                    .circle-container svg { width: 160px; height: 160px; }
                    .percent-text { font-size: 1.6rem; }
                    .s-num { font-size: 1rem; }
                    .s-lbl { font-size: 9px; }
                    .c-head { font-size: 10px; padding: 7px 0; }
                    .c-body { font-size: 1.1rem; padding: 9px 0; }
                    .panel-body-grid { grid-template-columns: 1fr; }
                    .m-icon-wrap { width: 40px; height: 40px; border-radius: 10px; }
                    .m-lbl { font-size: 10px; }
                    .menu-btn-square { padding: 14px 6px 12px; gap: 7px; }
                }

                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: 0.01ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: 0.01ms !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default LiveVotingForCader;
